import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  Clock,
  FileText,
  Loader2,
  Plus,
  Scale,
  Send,
  Trash2,
  Upload,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import {
  CourtFilingKindLabel,
  CourtFilingStatusLabel,
  CourtNotificationTypeLabel,
  type CourtAgentDto,
  type CourtAgentListResponse,
  type CourtFilingDto,
  type CourtFilingListResponse,
  type CourtFilingKindT,
  type CourtFilingStatusT,
  type CourtNotificationDto,
  type CourtNotificationListResponse,
  type CourtNotificationTypeT,
  type CreateCourtNotificationInput,
  type CreateCourtFilingInput,
} from '@toga/shared'


interface Props {
  caseId: string
  caseCode: string
  jurisdiction: string | null
  currentAgentId: string | null
  currentAgentName: string | null
  canWrite: boolean
  onAgentChanged?: () => void
}

export default function CaseLitigationPanel({
  caseId,
  caseCode,
  jurisdiction,
  currentAgentId,
  currentAgentName,
  canWrite,
  onAgentChanged,
}: Props) {
  const [notifications, setNotifications] = useState<CourtNotificationDto[]>([])
  const [filings, setFilings] = useState<CourtFilingDto[]>([])
  const [agents, setAgents] = useState<CourtAgentDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showNotif, setShowNotif] = useState(false)
  const [showFiling, setShowFiling] = useState(false)
  const [showAgent, setShowAgent] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [n, f, ag] = await Promise.all([
        api.get<CourtNotificationListResponse>(`/court-notifications?caseId=${caseId}`),
        api.get<CourtFilingListResponse>(`/court-filings?caseId=${caseId}`),
        api.get<CourtAgentListResponse>(`/court-agents`),
      ])
      setNotifications(n.items)
      setFilings(f.items)
      setAgents(ag.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void reload()
  }, [reload])

  const onChangeAgent = async (newId: string | null) => {
    try {
      await api.patch(`/cases/${caseId}`, { currentAgentId: newId })
      onAgentChanged?.()
      setShowAgent(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onMarkComplied = async (n: CourtNotificationDto) => {
    try {
      await api.patch(`/court-notifications/${n.id}`, { isComplied: true })
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onDeleteNotif = async (n: CourtNotificationDto) => {
    if (!confirm('¿Eliminar esta notificación?')) return
    try {
      await api.delete(`/court-notifications/${n.id}`)
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  const onFilingAction = async (f: CourtFilingDto, action: string) => {
    try {
      await api.post(`/court-filings/${f.id}/${action}`, {})
      await reload()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    }
  }

  return (
    <div className="space-y-4">
      {/* Procurador asignado */}
      <div className="surface p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="t-primary font-semibold flex items-center gap-2">
            <Briefcase size={14} className="text-toga-300" />
            Procurador
          </h3>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowAgent(true)}
              className="text-xs t-faint hover:text-toga-300"
            >
              {currentAgentId ? 'Cambiar' : 'Asignar'}
            </button>
          )}
        </div>
        {currentAgentName ? (
          <div className="text-sm t-primary">{currentAgentName}</div>
        ) : (
          <p className="text-xs t-faint">Sin procurador asignado.</p>
        )}
      </div>

      {/* Notificaciones */}
      <div className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold flex items-center gap-2">
            <Scale size={14} className="text-toga-300" />
            Notificaciones judiciales
            {notifications.length > 0 && (
              <span className="text-xs t-faint font-normal">({notifications.length})</span>
            )}
          </h3>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowNotif(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> Registrar
            </button>
          )}
        </div>

        {error && (
          <div className="text-xs text-rose-300 flex items-center gap-1 mb-2">
            <AlertCircle size={11} /> {error}
          </div>
        )}

        {loading ? (
          <div className="t-faint text-xs flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" /> Cargando…
          </div>
        ) : notifications.length === 0 ? (
          <p className="text-xs t-faint">Sin notificaciones todavía.</p>
        ) : (
          <ul className="divide-y divide-base/40 text-sm">
            {notifications.map((n) => (
              <li key={n.id} className="py-2.5">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-toga-500/15 text-toga-200">
                        {CourtNotificationTypeLabel[n.type]}
                      </span>
                      <span className="t-primary truncate">{n.title}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] t-muted">
                      <span>
                        Notificada: {new Date(n.notifiedAt).toLocaleDateString('es-ES')}
                      </span>
                      {n.deadlineDate && (
                        <DeadlinePill
                          dateIso={n.deadlineDate}
                          isComplied={n.isComplied}
                        />
                      )}
                      {n.attachmentKey && (
                        <a
                          href="#"
                          onClick={async (e) => {
                            e.preventDefault()
                            try {
                              const { url } = await api.get<{ url: string }>(
                                `/court-notifications/${n.id}/attachment`
                              )
                              window.open(url, '_blank')
                            } catch (err) {
                              alert(
                                err instanceof Error ? err.message : 'No se pudo abrir el documento'
                              )
                            }
                          }}
                          className="t-secondary hover:text-toga-300 inline-flex items-center gap-1"
                        >
                          <FileText size={11} /> {n.attachmentName ?? 'PDF'}
                        </a>
                      )}
                    </div>
                    {n.description && (
                      <p className="text-xs t-secondary mt-1">{n.description}</p>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-1">
                      {!n.isComplied && (
                        <button
                          type="button"
                          onClick={() => void onMarkComplied(n)}
                          className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/10 t-muted hover:text-emerald-300"
                          title="Marcar cumplida"
                        >
                          <CheckCircle2 size={12} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void onDeleteNotif(n)}
                        className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                        title="Eliminar"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Escritos */}
      <div className="surface p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="t-primary font-semibold flex items-center gap-2">
            <FileText size={14} className="text-toga-300" />
            Escritos
            {filings.length > 0 && (
              <span className="text-xs t-faint font-normal">({filings.length})</span>
            )}
          </h3>
          {canWrite && (
            <button
              type="button"
              onClick={() => setShowFiling(true)}
              className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
            >
              <Plus size={12} /> Nuevo
            </button>
          )}
        </div>

        {filings.length === 0 ? (
          <p className="text-xs t-faint">Sin escritos preparados.</p>
        ) : (
          <ul className="divide-y divide-base/40 text-sm">
            {filings.map((f) => (
              <li key={f.id} className="py-2.5">
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/40 text-slate-300">
                        {CourtFilingKindLabel[f.kind]}
                      </span>
                      <span className="t-primary truncate">{f.title}</span>
                      <FilingStatusPill status={f.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] t-muted">
                      {f.internalDueDate && (
                        <span className="flex items-center gap-1">
                          <Clock size={10} /> Antes de{' '}
                          {new Date(f.internalDueDate).toLocaleDateString('es-ES')}
                        </span>
                      )}
                      {f.documentKey && (
                        <a
                          href="#"
                          onClick={async (e) => {
                            e.preventDefault()
                            try {
                              const { url } = await api.get<{ url: string }>(
                                `/court-filings/${f.id}/document`
                              )
                              window.open(url, '_blank')
                            } catch (err) {
                              alert(
                                err instanceof Error ? err.message : 'No se pudo abrir el documento'
                              )
                            }
                          }}
                          className="t-secondary hover:text-toga-300 inline-flex items-center gap-1"
                        >
                          <FileText size={11} /> {f.documentName ?? 'PDF'}
                        </a>
                      )}
                    </div>
                  </div>
                  {canWrite && (
                    <FilingActions
                      filing={f}
                      onAction={(action) => void onFilingAction(f, action)}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showAgent && (
        <Modal open onClose={() => setShowAgent(false)} title="Asignar procurador">
          <ul className="space-y-1 max-h-96 overflow-y-auto">
            <li>
              <button
                type="button"
                onClick={() => void onChangeAgent(null)}
                className="w-full text-left px-3 py-2 rounded text-sm t-secondary hover:bg-hover border border-transparent"
              >
                — sin procurador —
              </button>
            </li>
            {agents.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => void onChangeAgent(a.id)}
                  className={[
                    'w-full text-left px-3 py-2 rounded text-sm border',
                    a.id === currentAgentId
                      ? 'bg-toga-500/15 text-toga-200 border-toga-500/30'
                      : 't-secondary hover:bg-hover border-transparent',
                  ].join(' ')}
                >
                  <div className="font-medium">{a.fullName}</div>
                  {a.judicialDistrict && (
                    <div className="text-xs t-faint">{a.judicialDistrict}</div>
                  )}
                </button>
              </li>
            ))}
            {agents.length === 0 && (
              <li className="text-xs t-faint text-center py-3">
                Aún no hay procuradores dados de alta.{' '}
                <a href="/procuradores" className="text-toga-300 hover:underline">
                  Crear el primero
                </a>
              </li>
            )}
          </ul>
        </Modal>
      )}

      {showNotif && (
        <NotificationModal
          caseId={caseId}
          jurisdiction={jurisdiction}
          onClose={() => setShowNotif(false)}
          onSaved={async () => {
            setShowNotif(false)
            await reload()
          }}
        />
      )}

      {showFiling && (
        <FilingModal
          caseId={caseId}
          notifications={notifications}
          onClose={() => setShowFiling(false)}
          onSaved={async () => {
            setShowFiling(false)
            await reload()
          }}
        />
      )}

      {/* Suprime warning unused */}
      <span className="hidden">{caseCode}</span>
    </div>
  )
}

function DeadlinePill({ dateIso, isComplied }: { dateIso: string; isComplied: boolean }) {
  const date = new Date(dateIso)
  const days = Math.ceil((date.getTime() - Date.now()) / (24 * 3600 * 1000))
  const overdue = days < 0
  const cls = isComplied
    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : overdue
      ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
      : days <= 3
        ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
        : 'bg-slate-500/15 text-slate-300 border-slate-500/30'
  const label = isComplied
    ? `Cumplida · ${date.toLocaleDateString('es-ES')}`
    : overdue
      ? `Vencido hace ${Math.abs(days)} d`
      : days === 0
        ? `Vence HOY`
        : `Vence en ${days} d`
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${cls}`}
    >
      <Clock size={9} /> {label} ({date.toLocaleDateString('es-ES')})
    </span>
  )
}

function FilingStatusPill({ status }: { status: CourtFilingStatusT }) {
  const STYLES: Record<CourtFilingStatusT, string> = {
    BORRADOR: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
    ENVIADO_A_PROCURADOR: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
    PRESENTADO: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    ACEPTADO: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    RECHAZADO: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    RESUELTO: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  }
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}
    >
      {CourtFilingStatusLabel[status]}
    </span>
  )
}

function FilingActions({
  filing,
  onAction,
}: {
  filing: CourtFilingDto
  onAction: (action: string) => void
}) {
  if (filing.status === 'BORRADOR') {
    return (
      <button
        type="button"
        onClick={() => onAction('send-to-agent')}
        className="px-2 py-1 rounded text-[11px] t-secondary border border-base hover:bg-hover inline-flex items-center gap-1"
        title="Marcar como enviado al procurador"
      >
        <Send size={11} /> Enviar
      </button>
    )
  }
  if (filing.status === 'ENVIADO_A_PROCURADOR') {
    return (
      <button
        type="button"
        onClick={() => onAction('mark-presented')}
        className="px-2 py-1 rounded text-[11px] t-secondary border border-base hover:bg-hover"
      >
        Presentado
      </button>
    )
  }
  if (filing.status === 'PRESENTADO') {
    return (
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onAction('mark-accepted')}
          className="px-2 py-1 rounded text-[11px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10"
        >
          Aceptado
        </button>
        <button
          type="button"
          onClick={() => onAction('mark-rejected')}
          className="px-2 py-1 rounded text-[11px] text-rose-300 border border-rose-500/30 bg-rose-500/10"
        >
          Rechazado
        </button>
      </div>
    )
  }
  if (filing.status === 'ACEPTADO' || filing.status === 'RECHAZADO') {
    return (
      <button
        type="button"
        onClick={() => onAction('mark-resolved')}
        className="px-2 py-1 rounded text-[11px] t-secondary border border-base hover:bg-hover"
      >
        Resuelto
      </button>
    )
  }
  return null
}

function NotificationModal({
  caseId,
  jurisdiction,
  onClose,
  onSaved,
}: {
  caseId: string
  jurisdiction: string | null
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [type, setType] = useState<CourtNotificationTypeT>('PROVIDENCIA')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [notifiedAt, setNotifiedAt] = useState(new Date().toISOString().slice(0, 10))
  const [deadlineDays, setDeadlineDays] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Indica un título')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (file) {
        const fd = new FormData()
        fd.append('caseId', caseId)
        fd.append('type', type)
        fd.append('title', title)
        if (description) fd.append('description', description)
        fd.append('notifiedAt', new Date(notifiedAt).toISOString())
        if (jurisdiction) fd.append('jurisdiction', jurisdiction)
        if (deadlineDays) fd.append('deadlineDays', deadlineDays)
        fd.append('file', file)
        await api.upload('/court-notifications', fd)
      } else {
        const payload: CreateCourtNotificationInput = {
          caseId,
          type,
          title: title.trim(),
          description: description.trim() || null,
          notifiedAt: new Date(notifiedAt).toISOString(),
          jurisdiction: jurisdiction ?? null,
          deadlineDays: deadlineDays ? Number(deadlineDays) : null,
        }
        await api.post('/court-notifications', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title="Nueva notificación judicial" maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Tipo</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as CourtNotificationTypeT)}
              className={input}
            >
              {Object.entries(CourtNotificationTypeLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Fecha de notificación</span>
            <input
              type="date"
              value={notifiedAt}
              onChange={(e) => setNotifiedAt(e.target.value)}
              className={input}
              required
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted">Título</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Sentencia n.º 145/2026 — estima recurso"
            className={input}
            required
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Descripción (opcional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={input}
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">
            Plazo (días hábiles)
            <span className="t-faint"> — opcional. Se calcula la fecha automáticamente.</span>
          </span>
          <input
            type="number"
            min={0}
            max={365}
            value={deadlineDays}
            onChange={(e) => setDeadlineDays(e.target.value)}
            placeholder="20"
            className={input}
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Adjuntar PDF (opcional)</span>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-xs t-secondary"
            />
            {file && (
              <span className="text-xs t-faint inline-flex items-center gap-1">
                <Upload size={11} /> {file.name}
              </span>
            )}
          </div>
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Registrar
          </button>
        </div>
      </form>
    </Modal>
  )
}

function FilingModal({
  caseId,
  notifications,
  onClose,
  onSaved,
}: {
  caseId: string
  notifications: CourtNotificationDto[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [kind, setKind] = useState<CourtFilingKindT>('ESCRITO_ORDINARIO')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [internalDueDate, setInternalDueDate] = useState('')
  const [respondsToNotificationId, setResponds] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      setError('Indica un título')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (file) {
        const fd = new FormData()
        fd.append('caseId', caseId)
        fd.append('kind', kind)
        fd.append('title', title)
        if (description) fd.append('description', description)
        if (internalDueDate)
          fd.append('internalDueDate', new Date(internalDueDate).toISOString())
        if (respondsToNotificationId)
          fd.append('respondsToNotificationId', respondsToNotificationId)
        fd.append('file', file)
        await api.upload('/court-filings', fd)
      } else {
        const payload: CreateCourtFilingInput = {
          caseId,
          kind,
          title: title.trim(),
          description: description.trim() || null,
          internalDueDate: internalDueDate
            ? new Date(internalDueDate).toISOString()
            : null,
          respondsToNotificationId: respondsToNotificationId || null,
        }
        await api.post('/court-filings', payload)
      }
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title="Nuevo escrito procesal" maxWidth="max-w-xl">
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Tipo</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as CourtFilingKindT)}
              className={input}
            >
              {Object.entries(CourtFilingKindLabel).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">Fecha límite interna (al procurador)</span>
            <input
              type="date"
              value={internalDueDate}
              onChange={(e) => setInternalDueDate(e.target.value)}
              className={input}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted">Título</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Recurso de reposición contra auto…"
            className={input}
            required
          />
        </label>
        <label className="block">
          <span className="text-xs t-muted">Descripción (opcional)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={input}
          />
        </label>
        {notifications.length > 0 && (
          <label className="block">
            <span className="text-xs t-muted">Responde a notificación (opcional)</span>
            <select
              value={respondsToNotificationId}
              onChange={(e) => setResponds(e.target.value)}
              className={input}
            >
              <option value="">— ninguna —</option>
              {notifications.map((n) => (
                <option key={n.id} value={n.id}>
                  {CourtNotificationTypeLabel[n.type]}: {n.title}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-xs t-muted">Adjuntar borrador (PDF/Word, opcional)</span>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-xs t-secondary mt-1"
          />
        </label>

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            Crear
          </button>
        </div>
      </form>
    </Modal>
  )
}
